import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { reportApi, vacationApi, remoteApi, userApi, calendarApi } from '../utils/api';
import {
  Users, Palmtree, Laptop, Clock, CheckSquare, ChevronRight,
  AlertTriangle, Loader, Check, X, Calendar, TrendingUp, Sparkles, Download,
  AlertCircle, Info, Trophy, Medal
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { TourGuide } from '../components/TourGuide';
import { ManagerMonthlyDetail } from '../components/ManagerMonthlyDetail';
import { ManagerTeamBalances } from '../components/ManagerTeamBalances';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

const MANAGER_TOUR_STEPS = [
  {
    target: null,
    icon: Sparkles,
    title: 'Panel de Manager — Bienvenido',
    description: 'Desde aquí gestionas todo lo relacionado con tu equipo: aprobaciones, disponibilidad y fichajes.',
    placement: 'center',
  },
  {
    target: '[data-testid="manager-stats"]',
    title: 'Indicadores clave del equipo',
    description: 'Número de miembros, quién está de vacaciones hoy, quién trabaja en remoto y cuántas solicitudes esperan tu decisión.',
    placement: 'bottom',
  },
  {
    target: '[data-testid="manager-today-widget"]',
    title: 'Disponibilidad del equipo hoy',
    description: 'Vista rápida con código de colores: verde (vacaciones aprobadas), azul (teletrabajo aprobado), amarillo (pendientes).',
    placement: 'bottom',
  },
  {
    target: '[data-testid="manager-team-balances"]',
    title: 'Saldos del equipo',
    description: 'Consulta el saldo de vacaciones (total, usados, pendientes y disponibles) de cada persona de tu equipo, incluidos los reportes indirectos.',
    placement: 'top',
  },
  {
    target: '[data-testid="manager-monthly-detail"]',
    title: 'Detalle mensual por empleado',
    description: 'Consulta cuántos días de vacaciones y teletrabajo ha solicitado cada persona del equipo en el mes, y pulsa sobre cada fila para ver sus solicitudes una por una.',
    placement: 'top',
  },
  {
    target: '[data-testid="manager-pending-section"]',
    title: 'Aprobaciones pendientes',
    description: 'Aprueba o rechaza solicitudes de vacaciones y teletrabajo directamente desde aquí. El empleado recibe un email al instante.',
    placement: 'top',
  },
  {
    target: '[data-testid="notification-bell"]',
    title: 'Notificaciones en tiempo real',
    description: 'La campana te avisa al instante cuando un miembro del equipo ficha entrada o salida. Sin recargar la página.',
    placement: 'bottom',
  },
  {
    target: null,
    icon: CheckSquare,
    title: '¡Todo bajo control!',
    description: 'Recuerda que puedes acceder al calendario completo del equipo, al control de fichajes y a los informes IA desde el menú lateral.',
    placement: 'center',
  },
];

const StatusBadge = ({ status }) => {
  const { t } = useLanguage();
  const cls = { pending: 'status-pending', approved: 'status-approved', rejected: 'status-rejected', cancelled: 'status-cancelled' };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cls[status] || ''}`}>{t(status)}</span>;
};

const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
  <div
    className={`bg-card border border-border rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200 ${onClick ? 'cursor-pointer hover:-translate-y-0.5' : ''}`}
    onClick={onClick}
  >
    <div className={`inline-flex p-2.5 rounded-lg ${color} mb-3`}>
      <Icon className="h-5 w-5" />
    </div>
    <div className="text-2xl font-bold font-heading">{value}</div>
    <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
  </div>
);

export default function ManagerDashboard() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [pendingVac, setPendingVac] = useState([]);
  const [pendingRem, setPendingRem] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamChartData, setTeamChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [teamToday, setTeamToday] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [teleworkRanking, setTeleworkRanking] = useState([]);
  const [weeklyTT, setWeeklyTT] = useState(null);
  const [weeklyVac, setWeeklyVac] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [sumRes, vacRes, remRes, usersRes] = await Promise.all([
        reportApi.summary({ team_only: true }),   // show team stats, not global
        vacationApi.list({ status: 'pending', team_only: true }),
        remoteApi.list({ status: 'pending', team_only: true }),
        userApi.list({ team_only: true })   // filter to direct reports only (even for admins)
      ]);
      setSummary(sumRes.data);
      setPendingVac(vacRes.data.slice(0, 5));
      setPendingRem(remRes.data.slice(0, 5));
      setTeamMembers(usersRes.data);

      // Today's team status with color coding
      const today = new Date().toISOString().split('T')[0];
      try {
        const calRes = await calendarApi.events({ start_date: today, end_date: today, team_only: true });
        setTeamToday(calRes.data);
      } catch (e) { console.warn('Team calendar events error:', e); }

      // Load team alerts
      try {
        const alertsRes = await reportApi.teamAlerts();
        setAlerts(alertsRes.data.alerts || []);
      } catch (e) { console.warn('Team alerts error:', e); }

      // Load weekly TT calendar (N+2 depth)
      try {
        const weeklyRes = await remoteApi.teamWeekly(3);
        setWeeklyTT(weeklyRes.data);
      } catch (e) { console.warn('Weekly TT error:', e); }

      // Load weekly Vacation calendar (N+2 depth, próximas 4 semanas)
      try {
        const weeklyVacRes = await vacationApi.teamWeekly(4);
        setWeeklyVac(weeklyVacRes.data);
      } catch (e) { console.warn('Weekly Vac error:', e); }

      // Fetch balances for chart + ranking (up to 20 members)
      const members = usersRes.data.slice(0, 20);
      const balances = await Promise.allSettled(
        members.map(m => userApi.getBalance(m.id))
      );
      const chartData = members.map((m, i) => {
        const b = balances[i].status === 'fulfilled' ? balances[i].value.data : {};
        return {
          name: m.first_name,
          fullName: `${m.first_name} ${m.last_name}`,
          vacaciones: b.vacation_used || 0,
          teletrabajo: b.remote_used || 0,
        };
      });
      setTeamChartData(chartData);

      // Build telework ranking (sorted by teletrabajo DESC)
      const ranking = [...chartData]
        .sort((a, b) => b.teletrabajo - a.teletrabajo)
        .map((item, idx) => ({ ...item, rank: idx + 1 }));
      setTeleworkRanking(ranking);

    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (type, id) => {
    setProcessing(p => ({ ...p, [id]: 'approving' }));
    try {
      if (type === 'vacation') await vacationApi.approve(id, {});
      else await remoteApi.approve(id, {});
      await loadData();
    } catch (e) { console.error(e); }
    finally { setProcessing(p => ({ ...p, [id]: null })); }
  };

  const handleReject = async (type, id) => {
    const comment = window.prompt('Motivo del rechazo (opcional):') ?? '';
    setProcessing(p => ({ ...p, [id]: 'rejecting' }));
    try {
      if (type === 'vacation') await vacationApi.reject(id, { comment });
      else await remoteApi.reject(id, { comment });
      await loadData();
    } catch (e) { console.error(e); }
    finally { setProcessing(p => ({ ...p, [id]: null })); }
  };

  const handleExportExcel = async () => {
    setExporting(true);
    try {
      const res = await reportApi.exportExcel({ type: 'all' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `equipo_export_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) { console.error(e); }
    finally { setExporting(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader className="h-6 w-6 animate-spin text-brand" />
    </div>
  );

  const allPending = [
    ...pendingVac.map(v => ({ ...v, kind: 'vacation' })),
    ...pendingRem.map(r => ({ ...r, kind: 'remote' }))
  ].sort((a, b) => a.created_at?.localeCompare(b.created_at));

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading">Panel de Manager</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Hola {user?.first_name}, aquí tienes el resumen de tu equipo
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleExportExcel}
          disabled={exporting}
          data-testid="btn-export-excel-manager"
          className="gap-2"
        >
          {exporting ? <Loader className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Exportar Excel
        </Button>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="manager-stats">
        <StatCard icon={Users} label="Miembros del equipo" value={summary?.total_users || 0}
          color="bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400" />
        <StatCard icon={Palmtree} label={t('onVacationToday')} value={summary?.on_vacation_today || 0}
          color="bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400" />
        <StatCard icon={Laptop} label={t('remoteToday')} value={summary?.remote_today || 0}
          color="bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400" />
        <StatCard icon={Clock} label={t('pendingApprovals')} value={summary?.total_pending || 0}
          color="bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-400"
          onClick={() => navigate('/approvals')} />
      </div>

      {/* Proactive Alerts */}
      {alerts.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="manager-alerts">
          <h2 className="font-semibold font-heading text-sm mb-3 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-500" />
            Alertas del equipo
            <span className="bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-xs px-1.5 py-0.5 rounded-full font-medium">
              {alerts.length}
            </span>
          </h2>
          <div className="space-y-2">
            {alerts.map((alert) => {
              const severityStyles = {
                critical: 'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/10 dark:border-red-800 dark:text-red-300',
                warning: 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/10 dark:border-amber-800 dark:text-amber-300',
                info: 'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/10 dark:border-blue-800 dark:text-blue-300',
              };
              const Icon = alert.severity === 'critical' ? AlertCircle : alert.severity === 'warning' ? AlertTriangle : Info;
              return (
                <div key={alert.message} className={`flex items-start gap-2.5 p-3 rounded-lg border text-xs ${severityStyles[alert.severity] || severityStyles.info}`}>
                  <Icon className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
                  <span>{alert.message}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Today's team availability — color-coded */}
      {teamToday.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="manager-today-widget">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold font-heading text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4 text-brand" />
              Disponibilidad del equipo hoy
            </h2>
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-green-500 inline-block" />Vac. aprobadas</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-yellow-400 inline-block" />Vac. pendiente</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-sky-500 inline-block" />Teletrabajo aprobado</span>
              <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-amber-400 inline-block" />Teletrabajo pendiente</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {teamToday.map((ev) => {
              const colorMap = {
                vacation_approved: 'bg-green-100 text-green-800 border-green-300 dark:bg-green-900/20 dark:text-green-300 dark:border-green-700',
                vacation_pending: 'bg-yellow-100 text-yellow-800 border-yellow-300 dark:bg-yellow-900/20 dark:text-yellow-300 dark:border-yellow-700',
                remote_work_approved: 'bg-sky-100 text-sky-800 border-sky-300 dark:bg-sky-900/20 dark:text-sky-300 dark:border-sky-700',
                remote_work_pending: 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-700',
              };
              const key = `${ev.type}_${ev.status}`;
              return (
                <span key={`${ev.user_id}_${ev.type}_${ev.status}`} className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${colorMap[key] || 'bg-muted text-muted-foreground border-border'}`}>
                  {ev.type === 'vacation' ? <Palmtree className="h-3 w-3" /> : <Laptop className="h-3 w-3" />}
                  {ev.user_name}
                </span>
              );
            })}
          </div>
          <div className="mt-3">
            <button onClick={() => navigate('/calendar')} className="text-xs text-brand hover:underline font-medium flex items-center gap-1">
              Ver calendario completo <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      )}

      {/* Team TT Weekly Calendar — N+2 */}
      {weeklyTT && weeklyTT.team_size > 0 && (
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="manager-tt-weekly">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold font-heading text-sm flex items-center gap-2">
              <Laptop className="h-4 w-4 text-sky-500" />
              Teletrabajo del equipo — próximas 3 semanas
              <span className="text-xs font-normal text-muted-foreground">(N+2 · {weeklyTT.team_size} personas)</span>
            </h2>
            <div className="text-xs text-muted-foreground">
              Meta: máx. 50% por día
            </div>
          </div>
          <div className="space-y-3">
            {weeklyTT.weeks?.map((week) => {
              const dayLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'];
              const dayKeys = Object.keys(week.days || {}).sort();
              return (
                <div key={week.start} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">{week.week_label}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      week.peak_pct > 50
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                        : week.peak_pct > 30
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                          : 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                    }`}>
                      Pico: {week.peak_tt}/{weeklyTT.team_size} ({week.peak_pct}%)
                    </span>
                  </div>
                  <div className="grid grid-cols-5 gap-1.5">
                    {dayKeys.slice(0, 5).map((dayKey, idx) => {
                      const dayPeople = week.days[dayKey] || [];
                      const pct = weeklyTT.team_size > 0 ? Math.round(dayPeople.length / weeklyTT.team_size * 100) : 0;
                      const isOver = pct > 50;
                      return (
                        <div
                          key={dayKey}
                          title={dayPeople.map(p => p.user_name).join(', ') || 'Ninguno en TT'}
                          className={`relative p-2 rounded-lg border text-center cursor-default transition-colors ${
                            dayPeople.length === 0
                              ? 'bg-muted/20 border-border/30'
                              : isOver
                                ? 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800'
                                : 'bg-sky-50 border-sky-200 dark:bg-sky-900/10 dark:border-sky-800'
                          }`}
                        >
                          <div className="text-xs font-semibold text-muted-foreground">{dayLabels[idx]}</div>
                          <div className={`text-base font-bold font-heading ${
                            dayPeople.length === 0 ? 'text-muted-foreground/40'
                              : isOver ? 'text-red-600 dark:text-red-400'
                                : 'text-sky-600 dark:text-sky-400'
                          }`}>{dayPeople.length}</div>
                          {dayPeople.length > 0 && (
                            <div className={`text-xs ${isOver ? 'text-red-500' : 'text-sky-500'}`}>{pct}%</div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-sky-200 inline-block" />TT dentro del objetivo (≤50%)</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-red-200 inline-block" />Supera el 50%</span>
          </div>
        </div>
      )}

      {/* Team Vacation Weekly Calendar — N+2 */}
      {weeklyVac && weeklyVac.team_size > 0 && (
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="manager-vac-weekly">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold font-heading text-sm flex items-center gap-2">
              <Palmtree className="h-4 w-4 text-emerald-500" />
              Vacaciones del equipo — próximas 4 semanas
              <span className="text-xs font-normal text-muted-foreground">(N+2 · {weeklyVac.team_size} personas)</span>
            </h2>
            <div className="text-xs text-muted-foreground">
              Detecta solapes y huecos de cobertura
            </div>
          </div>
          <div className="space-y-3">
            {weeklyVac.weeks?.map((week) => {
              const dayLabels = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie'];
              const dayKeys = Object.keys(week.days || {}).sort();
              return (
                <div key={week.start} className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-muted-foreground">{week.week_label}</span>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                      week.peak_pct > 50
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400'
                        : week.peak_pct > 30
                          ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400'
                          : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'
                    }`}>
                      Pico: {week.peak_vac}/{weeklyVac.team_size} ({week.peak_pct}%)
                    </span>
                  </div>
                  <div className="grid grid-cols-5 gap-1.5">
                    {dayKeys.slice(0, 5).map((dayKey, idx) => {
                      const dayPeople = week.days[dayKey] || [];
                      const pct = weeklyVac.team_size > 0 ? Math.round(dayPeople.length / weeklyVac.team_size * 100) : 0;
                      const isOver = pct > 50;
                      const hasPending = dayPeople.some(p => p.status === 'pending' || p.status === 'cancellation_requested');
                      return (
                        <div
                          key={dayKey}
                          title={dayPeople.length === 0
                            ? 'Nadie de vacaciones'
                            : dayPeople.map(p => `${p.user_name}${p.status !== 'approved' ? ' (pdte.)' : ''}`).join(', ')}
                          className={`relative p-2 rounded-lg border text-center cursor-default transition-colors ${
                            dayPeople.length === 0
                              ? 'bg-muted/20 border-border/30'
                              : isOver
                                ? 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800'
                                : 'bg-emerald-50 border-emerald-200 dark:bg-emerald-900/10 dark:border-emerald-800'
                          }`}
                        >
                          <div className="text-xs font-semibold text-muted-foreground">{dayLabels[idx]}</div>
                          <div className={`text-base font-bold font-heading ${
                            dayPeople.length === 0 ? 'text-muted-foreground/40'
                              : isOver ? 'text-red-600 dark:text-red-400'
                                : 'text-emerald-600 dark:text-emerald-400'
                          }`}>{dayPeople.length}</div>
                          {dayPeople.length > 0 && (
                            <div className={`text-xs ${isOver ? 'text-red-500' : 'text-emerald-500'}`}>{pct}%</div>
                          )}
                          {hasPending && (
                            <span
                              className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-amber-500"
                              title="Hay solicitudes pendientes"
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-emerald-200 inline-block" />Vacaciones aprobadas</span>
            <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded bg-red-200 inline-block" />Más del 50% del equipo</span>
            <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-amber-500 inline-block" />Solicitudes pendientes</span>
          </div>
        </div>
      )}

      {/* Monthly per-employee detail — solves "cuántos días ha solicitado cada persona" */}
      <ManagerMonthlyDetail />

      {/* Team balances — read-only, includes full org subtree (approvers/dept heads) */}
      <ManagerTeamBalances />

      {/* Pending approvals + team */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="manager-pending-section">
        {/* Pending approvals */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold font-heading text-sm flex items-center gap-2">
              <Clock className="h-4 w-4 text-yellow-500" />
              {t('pendingApprovals')}
              {allPending.length > 0 && (
                <span className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 text-xs px-1.5 py-0.5 rounded-full font-medium">
                  {allPending.length}
                </span>
              )}
            </h2>
            <Button variant="ghost" size="sm" onClick={() => navigate('/approvals')} className="text-xs text-brand">
              Ver todas <ChevronRight className="h-3 w-3 ml-1" />
            </Button>
          </div>

          {allPending.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
              <CheckSquare className="h-8 w-8 mb-2 opacity-30" />
              <p className="text-sm">Todo al día. Sin solicitudes pendientes.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {allPending.map((req) => (
                <div key={req.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`h-8 w-8 rounded-lg flex-shrink-0 flex items-center justify-center ${req.kind === 'vacation' ? 'bg-green-100 dark:bg-green-900/30' : 'bg-blue-100 dark:bg-blue-900/30'}`}>
                      {req.kind === 'vacation' ? <Palmtree className="h-4 w-4 text-green-600 dark:text-green-400" /> : <Laptop className="h-4 w-4 text-blue-600 dark:text-blue-400" />}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{req.user_name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {req.kind === 'vacation'
                          ? `${req.start_date} → ${req.end_date} (${req.working_days}d)`
                          : `${req.date} · ${req.period_type === 'full_day' ? 'Día completo' : req.period_type === 'morning' ? 'Mañana' : 'Tarde'}`
                        }
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleApprove(req.kind, req.id)}
                      disabled={!!processing[req.id]}
                      data-testid={`approve-${req.id}`}
                      className="h-7 w-7 p-0 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20"
                    >
                      {processing[req.id] === 'approving' ? <Loader className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleReject(req.kind, req.id)}
                      disabled={!!processing[req.id]}
                      data-testid={`reject-${req.id}`}
                      className="h-7 w-7 p-0 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Team members */}
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
          <h2 className="font-semibold font-heading text-sm mb-4 flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            Mi Equipo ({teamMembers.length})
          </h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {teamMembers.map((member) => (
              <div key={member.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/50 transition-colors">
                <div className="h-8 w-8 rounded-full bg-brand flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {member.first_name?.[0]}{member.last_name?.[0]}
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{member.first_name} {member.last_name}</p>
                  <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                </div>
              </div>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-3 text-xs"
            onClick={() => navigate('/calendar')}
          >
            <Calendar className="h-3.5 w-3.5 mr-1.5" />
            Ver calendario del equipo
          </Button>
        </div>
      </div>

      {/* Team usage chart */}
      {teamChartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bar chart */}
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="manager-chart">
            <h2 className="font-semibold font-heading text-sm mb-4 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
              Uso por empleado (días consumidos)
            </h2>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={teamChartData} margin={{ top: 5, right: 5, left: -15, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} />
                <YAxis tick={{ fontSize: 11, fill: 'var(--muted-foreground)' }} allowDecimals={false} />
                <Tooltip
                  contentStyle={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, fontSize: 12 }}
                />
                <Bar dataKey="vacaciones" fill="#00a651" radius={[3, 3, 0, 0]} name="Vacaciones" />
                <Bar dataKey="teletrabajo" fill="#0ea5e9" radius={[3, 3, 0, 0]} name="Teletrabajo" />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex gap-4 mt-2 justify-center">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="w-3 h-3 rounded-sm bg-[#00a651] inline-block" />Vacaciones
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="w-3 h-3 rounded-sm bg-[#0ea5e9] inline-block" />Teletrabajo
              </div>
            </div>
          </div>

          {/* Telework ranking */}
          <div className="bg-card border border-border rounded-xl p-5 shadow-sm" data-testid="manager-telework-ranking">
            <h2 className="font-semibold font-heading text-sm mb-4 flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" />
              Ranking de teletrabajo del equipo
            </h2>
            <div className="space-y-2 max-h-52 overflow-y-auto">
              {teleworkRanking.map((member, idx) => {
                const medalColors = ['text-yellow-500', 'text-slate-400', 'text-amber-600'];
                return (
                  <div key={member.user_id || member.fullName} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted/30 transition-colors">
                    <div className={`w-6 text-center font-bold text-sm flex-shrink-0 ${medalColors[idx] || 'text-muted-foreground'}`}>
                      {idx < 3 ? <Medal className="h-4 w-4 inline" /> : `${idx + 1}`}
                    </div>
                    <div className="h-7 w-7 rounded-full bg-sky-100 dark:bg-sky-900/30 flex items-center justify-center text-sky-700 dark:text-sky-400 text-xs font-bold flex-shrink-0">
                      {member.fullName?.[0]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{member.fullName}</p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Laptop className="h-3 w-3 text-sky-500" />
                      <span className="text-xs font-semibold text-sky-600 dark:text-sky-400">{member.teletrabajo}d</span>
                    </div>
                  </div>
                );
              })}
              {teleworkRanking.length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4">Sin datos de teletrabajo aún</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tour guiado del manager */}
      <TourGuide tourId={`manager_${user?.id || 'mgr'}`} steps={MANAGER_TOUR_STEPS} enabled />
    </div>
  );
}
